import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const safeName = (s) => String(s || "").replace(/[<>:"/\\|?*]+/g, "").trim();

function fmtDetails(details) {
  // Make PDF-friendly (avoid arrows / weird quotes)
  const t = String(details || "")
    .replaceAll("→", "to")
    .replaceAll("•", "|")
    .replaceAll("’", "'")
    .replaceAll("“", '"')
    .replaceAll("”", '"');

  // Split into lines to wrap better
  // expected like: "U: ... | M: ..."
  return t
    .split("|")
    .map((x) => x.trim())
    .filter(Boolean)
    .join("\n");
}

function drawHeader(doc, meta) {
  const pageW = doc.internal.pageSize.getWidth();

  // Top blue bar
  doc.setFillColor(30, 77, 183); // #1E4DB7
  doc.rect(0, 0, pageW, 18, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("Car Idle Report", 10, 12);

  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  const month = meta?.month || "-";
  const company = meta?.companyLabel || "All Companies";
  const generated = meta?.generatedAt ? new Date(meta.generatedAt).toLocaleString() : new Date().toLocaleString();

  doc.text(`Month: ${month}`, 10, 26);
  doc.text(`Company: ${company}`, 10, 32);
  doc.text(`Generated: ${generated}`, 10, 38);

  // Divider line
  doc.setDrawColor(220, 220, 220);
  doc.line(10, 41, pageW - 10, 41);
}

function drawKpis(doc, summary) {
  const pageW = doc.internal.pageSize.getWidth();

  const startX = 10;
  const startY = 45;
  const gap = 6;
  const boxH = 14;
  const boxW = (pageW - 20 - gap * 3) / 4;

  const kpis = [
    { label: "Idle Cars", value: summary?.idleCars ?? 0 },
    { label: "Unassigned Idle Days (sum)", value: summary?.unassignedIdleDaysTotal ?? 0 },
    { label: "Manual Idle Days (sum)", value: summary?.manualIdleDaysTotal ?? 0 },
    { label: "Total Idle Days (sum)", value: summary?.totalIdleDaysTotal ?? 0 },
  ];

  kpis.forEach((k, i) => {
    const x = startX + i * (boxW + gap);
    const y = startY;

    doc.setFillColor(245, 247, 252); // light card
    doc.setDrawColor(220, 220, 220);
    doc.roundedRect(x, y, boxW, boxH, 2, 2, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(30, 77, 183);
    doc.text(String(k.value), x + 4, y + 9);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(70, 70, 70);
    doc.text(k.label, x + 4, y + 13);
  });

  // return next Y
  return startY + boxH + 6;
}

export function exportCarIdleToPDF(rows, summary, meta) {
  if (!rows || rows.length === 0) return;

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const month = meta?.month || "";
  const companyLabel = meta?.companyLabel || "All Companies";

  
  autoTable(doc, {
    startY: 45 + 14 + 10, // header drawn later per page; start below KPI block
    margin: { left: 8, right: 8 },
    theme: "grid",
    head: [[
      "Car",
      "Company",
      "Model",
      "Total",
      "Assigned",
      "Unassigned",
      "Manual",
      "Total Idle",
      "Util%",
      "Idle Details",
    ]],
    body: rows.map((r) => [
      r.carNumber || "-",
      r.company || "-",
      r.vehicleType || "-",
      String(r.totalDays ?? 0),
      String(r.assignedDays ?? 0),
      String(r.unassignedIdleDays ?? 0),
      String(r.manualIdleDays ?? 0),
      String(r.totalIdleDays ?? 0),
      `${Math.round(Number(r.utilization || 0) * 100)}%`,
      fmtDetails(r.idleDetailsText),
    ]),
    styles: {
      font: "helvetica",
      fontSize: 8.2,
      cellPadding: 1.3,
      valign: "middle",
      overflow: "linebreak",
      lineColor: [200, 200, 200],
      lineWidth: 0.15,
    },
    headStyles: {
      fillColor: [30, 77, 183],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 9,
    },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: {
      0: { cellWidth: 26 },  // Car
      1: { cellWidth: 55 },  // Company
      2: { cellWidth: 28 },  // Model
      3: { cellWidth: 12, halign: "center" },
      4: { cellWidth: 16, halign: "center" },
      5: { cellWidth: 18, halign: "center" },
      6: { cellWidth: 15, halign: "center" },
      7: { cellWidth: 16, halign: "center" },
      8: { cellWidth: 12, halign: "center" },
      9: { cellWidth: 90 },  // Details (wrapped)
    },
    didParseCell: (data) => {
      if (data.section !== "body") return;

      const rowIdx = data.row.index;
      const totalIdle = Number(rows[rowIdx]?.totalIdleDays || 0);

      // Heat highlight based on Total Idle Days
      if (totalIdle >= 15) {
        data.cell.styles.fillColor = [255, 232, 232]; // light red
      } else if (totalIdle >= 7) {
        data.cell.styles.fillColor = [255, 244, 229]; // light orange
      }

      // Make Total Idle bold
      if (data.column.index === 7) {
        data.cell.styles.fontStyle = "bold";
      }
    },
    didDrawPage: () => {
      // Header + KPIs on each page
      drawHeader(doc, meta);
      drawKpis(doc, summary);

      // Footer page numbers
      const pageCount = doc.internal.getNumberOfPages();
      const page = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `Page ${page} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 6,
        { align: "center" }
      );
    },
  });

  const fileName = safeName(`Car Idle Report_${month}_${companyLabel}.pdf`);
  doc.save(fileName);
}